import {HttpParams} from '@angular/common/http';
import {inject, Injectable, Signal} from '@angular/core';
import {queryOptions, skipToken} from '@tanstack/angular-query-experimental';
import {lastValueFrom, map, Observable} from 'rxjs';

import {AWSHttpClient} from '@intocare/aws';

import {Statistics} from './statistics.type';
import {statisticsQueryKeys} from './tanstack-query-keys';

@Injectable({
	providedIn: 'root'
})
export class StatisticsService {
	private readonly awsHttp = inject(AWSHttpClient);

	getAll(date: string = undefined): Observable<Statistics> {
		let params = new HttpParams();
		if (date !== undefined && date.length > 0) {
			params = params.set('date', date);
		}

		return this.awsHttp.get('/statistics', {params}).pipe(map((result: {statistics: Statistics}) => result.statistics));
	}

	getById(residentId: string, date: string = undefined): Observable<Statistics> {
		let params = new HttpParams();
		if (date !== undefined && date.length > 0) {
			params = params.set('date', date);
		}

		return this.awsHttp
			.get(`/statistics/${residentId}`, {params})
			.pipe(map((result: {statistics: Statistics}) => result.statistics));
	}

	public getAllStatisticsQueryOptions(dateSignal: Signal<string>) {
		return () => {
			return queryOptions({
				queryKey: statisticsQueryKeys.all(dateSignal()),
				queryFn: dateSignal() ? () => lastValueFrom(this.getAll(dateSignal())) : skipToken,
				staleTime: 10000
			});
		};
	}

	public getStatisticsQueryOptions(
		residentIdSignal: Signal<string>,
		dateSignal: Signal<string>,
		enabled: Signal<boolean>
	) {
		return () => {
			return queryOptions(
				residentIdSignal()
					? {
							queryKey: statisticsQueryKeys.byResidentId(residentIdSignal(), dateSignal()) as readonly string[],
							queryFn: () => lastValueFrom(this.getById(residentIdSignal(), dateSignal())),
							staleTime: 10000,
							enabled: enabled()
						}
					: {
							queryKey: statisticsQueryKeys.all(dateSignal()) as readonly string[],
							queryFn: () => lastValueFrom(this.getAll(dateSignal())),
							staleTime: 10000,
							enabled: enabled()
						}
			);
		};
	}
}
